import { RootState } from '@next/core-lib/app';
import { EntityState } from '@ngrx/entity';
import { Product } from 'src/app/shared/models/shared.model';

import { GenderTypes } from '../../feed-advice/models/feed-advice.model';

export interface FeedProfitModuleState extends RootState {
  calculation: CalculationState;
  calculations?: Calculation[];
  selectedCalculation?: Calculation;
  selectedCalculationId?: number;
  isLoaded: boolean;
  isChanged: boolean;
}

export type CalculationState = EntityState<Calculation>;

export interface Calculation {
  id: number;
  name: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  customerId: number;
  locationId: number;
  genders: GenderOptions;
  country: CountryOptions;
  feeders: Feeder[];
  calculatedFeeder: Feeder;
  companyDetails: CompanyDetails;
  baseLine: BaseLine;
  extendedBaseLine: ExtendedBaseLine;
  quickSimulations: QuickSimulation[];
  comparisonSimulation?: QuickSimulation;
  extendedComparisonSimulation?: ExtendedSimulation;
  extendedSimulation?: ExtendedSimulation;
}

export interface Feeder {
  id: number;
  type: FeederType;
  product: Product;
  genderFeedRatios: GenderFeedRatio[];
}

export interface GenderFeedRatio {
  name: GenderTypes;
  feed: number;
}

export interface CompanyDetails {
  cause: string;
  priceSetting: PriceSetting;
  numberOfSilos: number;
  amountOfGrowFinisher: number;
}

export enum PriceSetting {
  BRUTO = 'Bruto',
  NETTO = 'Netto',
}

export interface BaseLine {
  calculatedPriceLargerThan25Kg: number;
  calculatedPriceLessThan25Kg: number;
  averageWeightPerDeadGrowFinisher: number;
  growthStartProcedure: number;
  growthEndProcedure: number;
}

export interface ExtendedBaseLine {
  squareMeterPerGrowFinisher: number;
  startWeight: number;
  growFinisherPrice: number;
  revenuePerKg: number;
  slaughteredWeightInKg: number;
  meatPercentage: number;
  loinDepthThicknessInMillimeters: number;
  backFatThicknessInMillimeters: number;
  growth: number;
  averageEnergyConversion: number;
  mortalityPercentage: number;
  deliveries: BaselineDelivery[];
  cleaning: number;
  fertilizerPerGrowFinisherInCubicMeters: number;
  fertilizerPricePerCubicMeter: number;
}

export interface ExtendedSimulationExtendedBaseLine {
  squareMeterPerGrowFinisher: number;
  startWeight: number;
  growFinisherPrice: number;
  revenuePerKg: number;
  slaughteredWeightInKg: number;
  growth: number;
  averageEnergyConversion: number;
  mortalityPercentage: number;
  deliveries: BaselineDelivery[];
  cleaning: number;
  fertilizerPricePerCubicMeter: number;
  meatPercentage: number;
  feedPackagePrice: number;
  loinDepthThicknessInMillimeters: number;
  backFatThicknessInMillimeters: number;
  fertilizerPerGrowFinisherInCubicMeters: number;
}

export interface ExtendedSimulationTableRow {
  variableName: string;
  type: string;
  comparisonBaseSimulation: string;
  extendedSimulation: string;
  hasCurrency: boolean;
}

export interface ToBeAddedValues {
  startWeight?: number;
}

export interface BaselineDelivery {
  index: number;
  percentage: number;
  days: number;
}

export interface QuickSimulation {
  id?: number;
  startWeight: number;
  pigletPrice: number;
  slaughteredWeightInKg: number;
  revenuePerKg: number;
  calculatedFeedPackagePrice: number;
  feedPackagePrice: number;
}

export interface QuickSimulationTableRow {
  id?: number;
  startWeight: number;
  pigletPrice: number;
  slaughteredWeightInKg: number;
  revenuePerKg: number;
  feedPacketPrice?: number;

  isComparisonSim: boolean;
  showFullWidthColumn?: boolean;
  text?: string;

  occupancyRate?: number;
  amount?: number;
  growth?: number;
  feedConversion?: number;
  feedPrice?: number;
  finalDifferenceDelivered?: number;
  finalDifferenceAveragePresentGrowFinisher?: number;
  finalDifferenceYear?: number;
  feedProfitDelivered?: number;
  feedProfitYear?: number;
  feedProfitAverage?: number;
}

export interface ExtendedSimulation {
  genders: GenderOptions;
  country: CountryOptions;
  startWeight: number;
  pigletPrice: number;
  slaughteredWeightInKg: number;
  revenuePerKg: number;
  feedPackagePrice: number;

  feeders: Feeder[];
  calculatedFeeder: Feeder;
  baseLine: BaseLine;
  extendedBaseLine: ExtendedSimulationExtendedBaseLine;
}

export interface ExtendedSimulationData {
  comparison: ExtendedSimulationFullObject;
  extended: ExtendedSimulationFullObject;
}

export interface ExtendedSimulationFullObject {
  startWeight: number;
  pigletPrice: number;
  pigletPricePerKg?: number;
  slaughteredWeightInKg: number;
  livingWeight?: number;
  revenuePerKg: number;
  feedPackagePrice?: number;

  isComparisonSim: boolean;

  occupancyRate?: number;
  amount?: number;
  growth?: number;
  feedConversion?: number;
  feedPrice?: number;
  finalDifferenceDelivered?: number;
  finalDifferenceAveragePresentGrowFinisher?: number;
  finalDifferenceYear?: number;
  feedProfitDelivered?: number;
  feedProfitDeliveredPerKg?: number;
  feedProfitYear?: number;
  feedProfitAverage?: number;

  profitPerAverageDeliveredGrowFinisher?: number;

  avgAmountDeliveredGrowFinisher?: number;
  averageAmountOfPresentDeliveredGrowFinishers?: number;
  //startweight
  //slaughteredweight
  divergingDaysStartWeight?: number;
  divergingDaysSlaughteredWeight?: number;
  divergingDaysGrowth?: number;
  amountOfDays?: number;
  //growth?: number
  // occupancyrate
  mortalityPercentage?: number;
  meatPercentage?: number;
  milimeterMuscle?: number;
  milimeterNeck?: number;
  amountOfRounds?: number;
  divergingEnergyFromGrowthStartStartWeight?: number;
  divergingEnergyFromGrowthEndDeliveredWeight?: number;
  divergingEnergyDivergingGrowth?: number;
  energyConversion?: number;
  energyConversionBoarOrBarrow?: number;
  energyConversionGilt?: number;
  avergeEnergyConversion?: number;
  effectDifferentStartWeightOnFeedCosts?: number; //J26
  effectSlaughteredWeightOnFeedCost?: number;
  effectDifferenceGrowthOnFeedCost?: number;
  energyUse?: number;
  //feedPackagePrice?: number;
  packetTotalKg?: number;
  packetPricePerKg?: number;
  feedCostPerKgPerGrowth?: number;
  feedProfitPerDeliveredGrowFinisher?: number;
  profitPerGrowFinisher?: number;
  profitGrowFinisherPerKg?: number;
  feedCosts?: number;
  costDeathsAndSpoilage?: number;
  costDeathsAndSpoilagePerKg?: number;

  feedProfitAverageGrowFinisherInYear?: number;
  pigletPriceInYear?: number;
  feedCostsInYear?: number;
  costsDeathsAndSpoilageInYear?: number;
  feedprofitAverageGrowFinisherYear?: number;

  squareMeterPerGrowFinisher: number;
  fertilizerPricePerCubicMeter: number;
  manureCubedProduction?: number;
  fertilizerCostsPerGrowFinisher: number;
  feedProfitAverageGrowFinisherInclFertilizer: number;
  feedProfitPerSquareMeterInclFertilizer: number;
}

export interface ExtendedSimulationFeederComparison {
  Type: string;
  curCode: string;
  extCode: string;
  curEnergyValues: number;
  extEnergyValues: number;
  curLysine: number;
  extLysine: number;
  curProtein: number;
  extProtein: number;
  curRe: number;
  extRe: number;
  curGrossPrice: number;
  extGrossPrice: number;
  curNetPrice: number;
  extNetPrice: number;
  curKgFeed: number;
  extKgFeed: number;
  curFeedCosts: number;
  extFeedCosts: number;
}

export interface FeederTableRow {
  id: number;
  product?: Product;
  grossPricePer100Kg?: number;
  unitNettoPrice?: number;
  ewKg?: number;
  lysKg?: number;
  phosphorusGramPerKg?: number;
  proteinGramPerKg?: number;
  barrow?: number;
  boar?: number;
  gilt?: number;
  average?: number;
  percentage?: number;
  isCalculated?: boolean;
}

export interface FormValues {
  name?: string;
  createdAt?: string;
  genders?: string;
  country?: string;
  feeders?: Feeder[];
  calculatedFeeder?: Feeder;
  companyDetails?: CompanyDetails;
  baseLine?: BaseLine;
  extendedBaseLine?: ExtendedBaseLine;
  quickSimulations?: QuickSimulation[];
  comparisonSimulation?: QuickSimulation;
  extendedSimulation?: ExtendedSimulation;
  extendedComparisonSimulation?: ExtendedSimulation;
  toBeAddedValues?: ToBeAddedValues;
}

export interface GetFeedProfitCalculationsResponse {
  calculations: Calculation[];
}

export enum FeederType {
  START_FEEDER = 'Start',
  GROWTH_FEEDER = 'Growth',
  END_FEEDER = 'End',
}

export enum GenderOptions {
  BOAR_GILT = 'boar_gilt',
  BARROW_GILT = 'barrow_gilt',
}

export enum GrowFinisherTypes {
  BOAR = 'boar',
  BARROW = 'barrow',
  SOW = 'sow',
  GILT = 'gilt',
}

export enum CountryOptions {
  NL = 'nl',
  DU = 'du',
  BIO = 'bio',
}

export enum SimulationType {
  QUICK_SIMULATION = 'quick_simulation',
  EXTENDED_SIMULATION = 'extended_simulation',
  BOTH = 'both',
}
